// components/HeatmapLayer.tsx
import { useMap } from 'react-leaflet'
import { useEffect, useMemo } from 'react'
// leafletGlobal must be imported before 'leaflet.heat' so the plugin patches the
// same Leaflet instance we use here. See leafletGlobal.ts for the why.
import L from './leafletGlobal'
// eslint-disable-next-line no-restricted-imports -- the shim above is exactly what the rule asks for
import 'leaflet.heat'
import type React from 'react'

type HeatmapLayerProps = {
  points: [number, number, number?][] // [lat, lng, intensity?]
}

const HeatmapLayer: React.FC<HeatmapLayerProps> = ({ points }) => {
  const map = useMap()

  const heatLayer = useMemo(
    () =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (L as any)
        .heatLayer([], {
          minOpacity: 0.3,
          radius: 25,
          blur: 15 //0.1 * (points[0][2] || 150) // Default blur based on intensity
          // maxZoom: 20
        })
        .addTo(map),
    [map]
  )

  useEffect(() => {
    if (!heatLayer._map || !map) return

    heatLayer.setLatLngs(points)
    heatLayer.redraw()
  }, [points, heatLayer])

  useEffect(() => {
    const handleZoomEnd = () => {
      // Check if the layer still has a valid map reference before calling setOptions
      if (heatLayer._map && map) {
        const currentZoom = map.getZoom()
        const newRadius = 25 * Math.pow(1.2, currentZoom - 13) // Adjust base radius as needed
        const newBlur = 15 * Math.pow(1.1, currentZoom - 13)
        heatLayer.setOptions({ radius: newRadius, blur: newBlur })
      }
    }

    if (map) {
      map.on('zoomend', handleZoomEnd)
    }

    return () => {
      if (map) {
        map.off('zoomend', handleZoomEnd)
      }
      if (map.hasLayer(heatLayer)) {
        map.removeLayer(heatLayer)
      } else console.log('Heatmap layer not found on map during cleanup.')
    }
  }, [map])

  return null
}

export default HeatmapLayer
